﻿module Illy.SafeStack.Client.Grid

    open Elmish
    open Fable.React
    open Fable.React.Props
    open Illy.SafeStack.Client.jQueryUI
    open Illy.SafeStack.Client.ActionButton

    type 'a GridButton =
        {
            Button: 'a ActionButton
            DisplayInInfo: bool
        }

    module GridButton =
        let instance label id icon disabled disabledTitle displayInInfo =
            {
                Button = ActionButton.instance label id icon disabled disabledTitle 
                DisplayInInfo = displayInInfo
            }
            
        let add itemName id icon disabled disabledTitle displayInInfo =
            {
                Button = ActionButton.add itemName id icon disabled disabledTitle 
                DisplayInInfo = displayInInfo
            }
            
        let addStandard itemName id displayInInfo =
            {
                Button = ActionButton.addStandard itemName id
                DisplayInInfo = displayInInfo
            }

        let addConditionallyDisabled itemName id disabled disabledTitle displayInInfo =
            {
                Button = ActionButton.addConditionallyDisabled itemName id disabled disabledTitle 
                DisplayInInfo = displayInInfo
            }

    type 'a GridSideEffect =
        | ButtonClicked of 'a

    type 'a Msg =
        | ActionMsg of 'a ActionButton.Msg
        | DropdownMsg of 'a DropdownButton.Msg

    type Model<'a,'b> =
        {
            Data: 'a list
            Dropdown: 'b DropdownButton.Model
            SingularName: string
            PluralName: string
            NoItemsMessage: string
            Buttons: 'b GridButton list
            Count: int
            RenderCount: int -> string -> string -> string
        }

    let getButtons gridButtons = gridButtons |> List.map (fun x -> x.Button)

    let gridControls loading singularName pluralName counter count dispatch (buttons: 'a ActionButton list) =
        let formattedCount = counter count singularName pluralName

        div [ Class "gridControls" ]
            [
                span [ Class <| addSkeletonScreen "count" None loading ] [ str formattedCount ]
                div [ Class "controls" ]
                    [
                        for button in buttons do
                            yield ActionButton.view button (ActionMsg >> dispatch)
                    ]
            ]

    let init singularName pluralName noItemsMessage buttons =
        {
            Dropdown = DropdownButton.init (buttons |> getButtons)
            SingularName = singularName
            PluralName = pluralName
            NoItemsMessage = noItemsMessage
            Buttons = buttons
            Count = 0
            Data = []
            RenderCount =
                fun count singular plural ->
                    match count with
                    | 0 -> ""
                    | 1 -> sprintf "1 %s found" singular
                    | _ -> sprintf "%d %s found" count plural
        }

    let initWithCustomCount counter singularName pluralName noItemsMessage buttons =
        {
            Dropdown = DropdownButton.init (buttons |> getButtons)
            SingularName = singularName
            PluralName = pluralName
            NoItemsMessage = noItemsMessage
            Buttons = buttons
            Count = 0
            Data = []
            RenderCount = counter
        }

    let updateData data count model =
        { model with
            Count = count
            Data = data
        }

    let updateButtons buttons model =
        { model with
            Buttons = buttons
            Dropdown = DropdownButton.updateButtons (buttons |> getButtons) model.Dropdown
        }

    let update msg model =
        match msg with
        | ActionMsg (ActionButton.Msg.ButtonClicked id) ->
            model, SideEffect<_,_>.Event (ButtonClicked id)
        | DropdownMsg msg ->
            let newModel, newCmd = DropdownButton.update msg model.Dropdown
            { model with Dropdown = newModel },
                match newCmd with
                | SideEffect.Cmd cmd -> SideEffect.Cmd <| Cmd.map DropdownMsg cmd
                | SideEffect.Event (DropdownButton.ClickSideEffect.ButtonClicked id) -> SideEffect.Event (ButtonClicked id)

    let view model columns loading dispatch =
        [
            if loading || model.Count > 0 then
                yield gridControls loading model.SingularName model.PluralName model.RenderCount model.Count dispatch (model.Buttons |> getButtons)
                yield simpleTable model.NoItemsMessage columns loading model.Data
            else
                yield div [ Class "illy-info illy-info-flex" ]
                    [
                        str model.NoItemsMessage
                        div [ Class "info-controls" ]
                            [
                                let infoButtons = model.Buttons |> List.filter (fun x -> x.DisplayInInfo) |> getButtons
                                if infoButtons.Length = 1 then
                                    yield ActionButton.view infoButtons.Head (ActionMsg >> dispatch)
                                else
                                    yield DropdownButton.view model.Dropdown (DropdownMsg >> dispatch)
                            ]
                    ]
        ]